import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { playerAvatar, wrestlerAvatar } from "../lib/api";
import AvatarLayered from "../components/AvatarLayered";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Users2, X, History, Crown, Link as LinkIcon, Swords, Skull, Landmark, BicepsFlexed, BarChart3, ChevronDown, Handshake, CalendarDays } from "lucide-react";
import { GradCard, SectionHeading, CountUp } from "../components/wrestler/WrestlerUI";

const BG1 = "/backgroundgraphics/Wrestlerpage_Background.webp";
const BG2 = "/backgroundgraphics/Wrestlerpage_Background2.webp";
const HALF_HOUR_MS = 30 * 60 * 1000;

function OnlineDot({ lastSeen, testId }) {
  const online = lastSeen && (Date.now() - new Date(lastSeen).getTime()) < HALF_HOUR_MS;
  const color = online ? "#39FF14" : "#FF007F";
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-[#0A051A]/70" style={{ borderColor: `${color}66` }} data-testid={testId}>
      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: color, boxShadow: `0 0 8px ${color}, 0 0 14px ${color}88`, animation: online ? "tt-blink 1.6s ease-in-out infinite" : undefined }} />
      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color }}>{online ? "Online" : "Offline"}</span>
    </span>
  );
}

function TeamStat({ icon: Icon, label, value, color, testId, extra, className = "" }) {
  return (
    <div className={`wr-stat group relative ${className} flex items-center gap-2.5 rounded-xl px-3 py-2.5 border-2 bg-[#0A051A]/80 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5`}
         style={{ borderColor: `${color}55`, "--wr-glow": color }}>
      <Icon size={30} strokeWidth={2.5} className="shrink-0 transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-110" style={{ color, filter: `drop-shadow(0 0 7px ${color})` }} />
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#C9BDF0] flex items-center gap-1">{label}{extra}</div>
        <CountUp value={value} testId={testId} className="block font-num text-[30px] leading-none tracking-wider" style={{ color, textShadow: `0 0 10px ${color}88, 2px 2px 0 #000` }} />
      </div>
    </div>
  );
}

function ChampionBanner({ since }) {
  const belt = (
    <img src="/pvp/title_tag_team.png" alt="Tag Team Title" className="w-14 h-14 object-contain"
         style={{ filter: "drop-shadow(0 0 10px #FFD700) drop-shadow(0 0 18px #FF9500AA)" }} />
  );
  return (
    <div className="relative mb-4 py-2 px-3 flex items-center justify-center gap-3 rounded-xl border-2" data-testid="tt-champion-banner"
         style={{ borderColor: "#FFD700", background: "linear-gradient(90deg, #FFD70022, #FF950033, #FFD70022)", animation: "tt-champion-pulse 2.4s ease-in-out infinite" }}>
      {belt}
      <div className="text-center">
        <div className="font-graffiti text-base uppercase text-[#FFD700]" style={{ textShadow: "0 0 8px #FFD700, 2px 2px 0 #000" }}>Reigning Tag Team Champions</div>
        <div className="font-mono text-[10px] text-[#FF9500]">Holding the belts since {since ? new Date(since).toLocaleDateString() : "—"}</div>
      </div>
      {belt}
    </div>
  );
}

function UnitMember({ m, w, border, onClick }) {
  return (
    <button onClick={onClick} className="relative group flex flex-col items-center" data-testid={`tt-unit-${m.account_name}`}>
      <div className="relative">
        <div className="rounded-xl p-[3px] transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-3" style={{ background: "linear-gradient(140deg, #00F0FF, #5B3BFF, #FF007F)", boxShadow: "0 0 20px rgba(91,59,255,0.45)" }}>
          <AvatarLayered fgSrc={playerAvatar(m.account_name, m.player_avatar_updated_at)} promo={m.promotion || (w && w.owner_promotion)} alt={m.account_name}
                         className="w-[100px] h-[100px] sm:w-[140px] sm:h-[140px] rounded-[10px]" />
        </div>
        {w && (
          <div className="absolute -bottom-1.5 -right-1.5 w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 overflow-hidden bg-[#0A051A]" style={{ borderColor: border, boxShadow: `0 0 10px ${border}88` }} title={w.name}>
            <AvatarLayered fgSrc={wrestlerAvatar(w)} promo={w.owner_promotion} alt={w.name} className="w-full h-full" />
          </div>
        )}
      </div>
      <div className="mt-2 font-graffiti text-base sm:text-lg uppercase leading-none text-white" style={{ textShadow: "0 0 10px #00F0FF99, 2px 2px 0 #000" }}>{m.account_name}</div>
      {w && <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest text-[#C9BDF0]">{w.name}</div>}
    </button>
  );
}

function UnitLink({ border }) {
  return (
    <div className="flex flex-col items-center justify-center text-center" aria-hidden="true">
      <LinkIcon size={30} strokeWidth={2.8} className="text-[#FFD700] tt-link-pulse" style={{ filter: `drop-shadow(0 0 8px ${border})` }} />
      <div className="mt-1 h-[3px] w-12 sm:w-20 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${border}, transparent)`, boxShadow: `0 0 8px ${border}` }} />
      <div className="mt-1.5 font-num text-lg tracking-widest whitespace-nowrap text-[#FFD700]" style={{ textShadow: "0 0 8px #FFD70088, 2px 2px 0 #000" }}>TAG TEAM</div>
    </div>
  );
}

function UnitStrip({ team, border, onProfile, isMember, onDisband }) {
  return (
    <div className="relative mb-5 py-5 rounded-xl border-2 overflow-hidden" style={{ borderColor: `${border}66`, background: "rgba(10,5,26,0.65)" }}>
      <div className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-60" style={{ backgroundImage: "url('/backgroundgraphics/Tag%20Team%20Page%20Banner.webp')" }} aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,5,26,0.3) 0%, rgba(10,5,26,0.65) 100%)" }} aria-hidden="true" />
      <div className="relative z-10 flex items-center justify-center gap-3 sm:gap-10 w-full">
        {team.members.map((m, i) => (
          <React.Fragment key={m.user_id}>
            <UnitMember m={m} w={team.wrestlers[i]} border={border} onClick={() => onProfile(m.account_name)} />
            {i === 0 && <UnitLink border={border} />}
          </React.Fragment>
        ))}
      </div>
      <div className="relative z-10 mt-4 px-3 sm:px-4 flex items-center justify-between gap-2 sm:mt-0 sm:absolute sm:inset-x-0 sm:bottom-3">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#0A051A]/70 text-[10px] sm:text-[11px] font-mono uppercase tracking-wider sm:tracking-widest whitespace-nowrap text-[#C9BDF0]" data-testid="tt-founded">
          <CalendarDays size={13} className="text-[#00F0FF]" /> Founded: {team.founded_at ? new Date(team.founded_at).toLocaleDateString() : "—"}
        </div>
        {isMember && (
          <button data-testid="tt-disband" onClick={onDisband} className="inline-flex items-center gap-1.5 shrink-0 px-2.5 sm:px-3 py-1.5 rounded-lg border-2 border-[#FF007F]/60 bg-[#0A051A]/70 text-[#FF007F] text-xs font-heading uppercase tracking-widest transition-[background-color,box-shadow,transform] hover:bg-[#FF007F]/15 hover:shadow-[0_0_14px_#FF007F66] active:scale-95">
            <X size={13} strokeWidth={3} /> Disband
          </button>
        )}
      </div>
    </div>
  );
}

function MemberCard({ m, w, isMember, delay, onProfile }) {
  return (
    <GradCard delay={delay} testId={`tt-member-${m.account_name}`} bg={BG1}>
      <div className="flex items-center gap-4 mb-3">
        <div className="relative rounded-xl p-[3px] shrink-0" style={{ background: "linear-gradient(140deg, #00F0FF, #5B3BFF, #FF007F)", boxShadow: "0 0 20px rgba(91,59,255,0.45)" }}>
          <AvatarLayered fgSrc={playerAvatar(m.account_name, m.player_avatar_updated_at)} promo={m.promotion || (w && w.owner_promotion)} alt={m.account_name}
                         className="w-24 h-24 sm:w-28 sm:h-28 rounded-[10px] cursor-pointer transition-transform duration-300 hover:scale-[1.03]" onClick={() => onProfile(m.account_name)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A89BCC]">Manager</div>
          <button onClick={() => onProfile(m.account_name)} className="block font-graffiti text-2xl uppercase leading-tight text-white truncate max-w-full hover:text-[#00F0FF] transition-colors"
                  style={{ textShadow: "0 0 14px #00F0FF88, 2px 2px 0 #000" }}>{m.account_name}</button>
          {isMember && <div className="mt-1.5"><OnlineDot lastSeen={m.last_seen} testId={`tt-online-${m.account_name}`} /></div>}
        </div>
      </div>
      {w && <WrestlerStrip w={w} />}
    </GradCard>
  );
}

function WrestlerStrip({ w }) {
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-xl bg-[#0A051A]/85 border-2 border-[#39FF14]/45 shadow-[0_0_14px_rgba(57,255,20,0.15)]">
      <AvatarLayered fgSrc={wrestlerAvatar(w)} promo={w.owner_promotion} alt="" className="w-20 h-20 rounded-lg border-2 border-[#39FF14]" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A89BCC]">Wrestler</div>
        <div className="font-graffiti text-lg leading-tight text-white truncate" style={{ textShadow: "0 0 10px #FF007F88, 2px 2px 0 #000" }}>{w.name}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-black/50 border border-[#00F0FF]/50 font-num text-base leading-none tracking-wider text-[#00F0FF]">LV {w.level}</span>
          <span className="px-2 py-0.5 rounded-md bg-black/50 border border-[#FF007F]/50 font-num text-base leading-none tracking-wider text-[#FF007F]">PWR {w.stats?.power}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#C9BDF0]">{w.state?.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );
}

function MemberHistoryCard({ member, history, onProfile }) {
  return (
    <div className="rounded-xl border-2 border-[#FFE600]/35 bg-[#0A051A]/80 p-3" data-testid={`tt-history-${member.account_name}`}>
      <div className="flex items-center gap-2.5 mb-2 pb-2 border-b border-[#FFE600]/25">
        <AvatarLayered fgSrc={playerAvatar(member.account_name, member.player_avatar_updated_at)} promo={member.promotion} alt={member.account_name}
                       className="w-11 h-11 rounded-lg border-2 border-[#00F0FF] cursor-pointer" onClick={() => onProfile(member.account_name)} />
        <div className="flex-1 min-w-0">
          <div className="font-graffiti text-base uppercase leading-tight text-white truncate" style={{ textShadow: "0 0 10px #00F0FF88, 2px 2px 0 #000" }}>{member.account_name}</div>
          <div className="text-[9px] font-mono text-[#A89BCC] uppercase tracking-widest">{history.length} previous team{history.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      {history.length === 0 ? (
        <div className="text-[11px] font-mono text-[#A89BCC] px-1 py-2">No previous tag teams on record.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="text-[#A89BCC] uppercase tracking-widest text-[9px]">
              <tr>
                <th className="p-1.5 text-left">Team</th>
                <th className="p-1.5 text-left">Partner</th>
                <th className="p-1.5 text-right">W</th>
                <th className="p-1.5 text-right">L</th>
                <th className="p-1.5 text-right">T</th>
                <th className="p-1.5 text-right">HOF</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.history_id} className="border-t border-[#39194D] transition-colors hover:bg-[#1C0D40]" data-testid={`tt-history-row-${h.history_id}`}>
                  <td className="p-1.5 text-[#39FF14] truncate max-w-[120px]">{h.team_name}</td>
                  <td className="p-1.5 text-[#00F0FF] truncate max-w-[100px]">
                    <button className="hover:underline" onClick={() => onProfile(h.partner_account_name)}>{h.partner_account_name}</button>
                  </td>
                  <td className="p-1.5 text-right font-num text-sm tracking-wider text-[#39FF14]">{h.wins}</td>
                  <td className="p-1.5 text-right font-num text-sm tracking-wider text-[#FF007F]">{h.losses}</td>
                  <td className="p-1.5 text-right font-num text-sm tracking-wider text-[#FFD700]">{h.titles_won}</td>
                  <td className="p-1.5 text-right font-num text-sm tracking-wider text-[#B44DFF]">{h.hof_points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistorySection({ team, memberHistories, onProfile }) {
  const [open, setOpen] = useState(false);
  const toggle = (
    <button data-testid="tt-history-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? "Hide tag team history" : "Show tag team history"}
            className="ml-1 w-8 h-8 rounded-full flex items-center justify-center border-2 border-[#FFE600]/70 bg-[#0A051A]/80 text-[#FFE600] transition-[transform,box-shadow,background-color] duration-200 hover:bg-[#FFE600]/15 hover:shadow-[0_0_14px_#FFE60088] active:scale-90">
      <ChevronDown size={18} strokeWidth={3} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
  );
  return (
    <GradCard delay={420} testId="tt-history-block" bg={BG2}>
      <SectionHeading icon={History} color="#FFE600" className={open ? "mb-4" : ""} testId="tt-history-heading" action={toggle}>Tag Team History</SectionHeading>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 tt-unfold" data-testid="tt-history-content">
          {team.members.map((m) => (
            <MemberHistoryCard key={m.user_id} member={m} history={memberHistories[m.account_name] || []} onProfile={onProfile} />
          ))}
        </div>
      )}
    </GradCard>
  );
}

export default function TagTeamPage() {
  const { team_id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [memberHistories, setMemberHistories] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tag-teams/${team_id}`);
      const t = r.data;
      setTeam(t);
      const results = await Promise.all(
        (t.members || []).map((m) =>
          api
            .get(`/tag-teams/history/${encodeURIComponent(m.account_name)}`)
            .then((res) => ({ account: m.account_name, hist: res.data || [] }))
            .catch(() => ({ account: m.account_name, hist: [] })),
        ),
      );
      const map = {};
      results.forEach(({ account, hist }) => { map[account] = hist; });
      setMemberHistories(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Team not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [team_id, user?.account_name]);

  const disband = async () => {
    if (!window.confirm(`Disband ${team?.name}? This cannot be undone.`)) return;
    try {
      await api.post("/tag-teams/disband");
      toast.success("Tag Team disbanded");
      window.dispatchEvent(new Event("tag-team-refresh"));
      nav("/pvp");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-8 text-center font-graffiti text-2xl neon-cyan">LOADING TAG TEAM…</div>;
  if (!team) return <div className="p-8 text-center font-graffiti text-2xl neon-pink">NOT FOUND</div>;

  const isMember = !!user && (team.members || []).some(m => m.user_id === user.user_id);
  const isChampion = !!team.is_champion;
  const combinedPower = (team.wrestlers || []).reduce((sum, w) => sum + Number(w?.stats?.power || 0), 0);
  const unitBorder = isChampion ? "#FFD700" : "#39FF14";
  const toProfile = (acct) => nav(`/profile/${encodeURIComponent(acct)}`);

  return (
    <div className="relative max-w-5xl mx-auto px-4 py-4" data-testid="tag-team-page">
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden" aria-hidden="true">
        <div className="wr-spot absolute -top-32 left-[6%] w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(57,255,20,0.16), transparent 65%)" }} />
        <div className="wr-spot absolute top-56 right-[4%] w-[480px] h-[480px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,240,255,0.16), transparent 65%)", animationDelay: "2.5s" }} />
        <div className="wr-grain absolute inset-0" />
      </div>

      <div className="relative flex flex-col gap-5">
        <GradCard delay={0} testId="tt-header-card" bg={BG1}>
          {isChampion && <div className="pointer-events-none absolute -inset-4 opacity-30" style={{ background: "repeating-linear-gradient(45deg, rgba(255,215,0,0.08) 0 12px, transparent 12px 24px)" }} />}
          {isChampion && <ChampionBanner since={team.champion_since} />}

          <div className="relative flex flex-wrap items-center gap-3 mb-4">
            <Users2 size={34} strokeWidth={2.6} className="text-[#39FF14] shrink-0" style={{ filter: "drop-shadow(0 0 8px #39FF14)" }} />
            <h1 className="font-graffiti text-4xl sm:text-5xl uppercase leading-none text-white break-words" style={{ textShadow: `0 0 18px ${unitBorder}AA, 3px 3px 0 #000` }} data-testid="tt-name">{team.name}</h1>
            {team.together_bonus_active && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-heading uppercase tracking-widest bg-[#39FF14]/20 border border-[#39FF14] text-[#39FF14] shadow-[0_0_12px_#39FF1455]" data-testid="tt-bonus-active">
                <Handshake size={13} strokeWidth={2.6} /> Together Bonus Active
              </span>
            )}
          </div>

          <UnitStrip team={team} border={unitBorder} onProfile={toProfile} isMember={isMember} onDisband={disband} />

          <SectionHeading icon={BarChart3} color="#FF007F" small className="mb-3" testId="tt-stats-heading">Team Stats</SectionHeading>
          <div className="relative grid grid-cols-2 md:grid-cols-5 gap-3">
            <TeamStat icon={Swords} label="Wins" value={team.wins} color="#39FF14" testId="tt-wins" />
            <TeamStat icon={Skull} label="Losses" value={team.losses} color="#FF007F" testId="tt-losses" />
            <TeamStat icon={Crown} label="Tag Titles" value={team.titles_won} color="#FFD700" testId="tt-titles" extra={isChampion && <Crown size={11} className="text-[#FFD700]" />} />
            <TeamStat icon={Landmark} label="HOF Points" value={team.hof_points} color="#B44DFF" testId="tt-hof" />
            <TeamStat icon={BicepsFlexed} label="Power" value={combinedPower} color="#00F0FF" testId="tt-power" className="col-span-2 md:col-span-1" />
          </div>
        </GradCard>

        <div>
          <SectionHeading icon={Users2} color="#00F0FF" testId="tt-members-heading">Members</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {team.members.map((m, i) => (
              <MemberCard key={m.user_id} m={m} w={team.wrestlers[i]} isMember={isMember} delay={160 + i * 120} onProfile={toProfile} />
            ))}
          </div>
        </div>

        <HistorySection team={team} memberHistories={memberHistories} onProfile={toProfile} />
      </div>

      <style>{`
        @keyframes tt-champion-pulse {
          0%, 100% { box-shadow: 0 0 20px #FFD70088, inset 0 0 20px #FF950044; }
          50%      { box-shadow: 0 0 36px #FFD700DD, inset 0 0 30px #FF950088; }
        }
        @keyframes tt-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
        @keyframes tt-link { 0%, 100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.15) rotate(-8deg); } }
        .tt-link-pulse { animation: tt-link 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
